import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Plus, RefreshCw, Loader2, Camera, Mail, Eye } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import { EmptyPlaceholder } from '@/components/EmptyPlaceholder';
import { useAuth } from '@/context/auth';
import { hasSuperAdminAccess, hasOrgAdminAccess, hasRestAdminAccess } from '@/context/auth/types';
import EmailService, { EmailTemplate } from '@/api/email';
import { TemplateCustomizationModal, DeleteConfirmationDialog } from '@/components/email/templates';
import TemplateList from '@/components/email/templates/TemplateList';
import TemplateEditorView from '@/components/email/templates/TemplateEditorView';
import PreviewDialog from '@/components/email/preview/PreviewDialog';
import { capturePreviewDialogScreenshot, waitForPreviewDialog } from '@/utils/capturePreviewDialog';
import { processTemplatePreview } from '@/utils/processTemplateImage';
import { uploadTemplatePreview, deleteOldTemplatePreviews } from '@/services/templatePreviewService';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface TemplatesTabProps {
  onError?: (error: Error) => void;
}

type ViewMode = 'list' | 'create' | 'edit';

const TemplatesTab: React.FC<TemplatesTabProps> = ({ onError }) => {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState<ViewMode>('list');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Customization modal state for system templates
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [templateToCustomize, setTemplateToCustomize] = useState<EmailTemplate | null>(null);
  const [customizedDesignJson, setCustomizedDesignJson] = useState<Record<string, any> | null>(null);

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);

  // Preview dialog state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [resolvedPreview, setResolvedPreview] = useState<{
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: { id: string; email: string; first_name: string; last_name: string };
  } | null>(null);
  const [templateBeingPreviewed, setTemplateBeingPreviewed] = useState<EmailTemplate | null>(null);

  // Customer preview state (for real variable resolution)
  const [recentCustomers, setRecentCustomers] = useState<Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    total_orders?: number;
  }>>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [templateToPreview, setTemplateToPreview] = useState<EmailTemplate | null>(null);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);

  // Preview capture state
  const [capturingPreview, setCapturingPreview] = useState(false);
  const [captureStatus, setCaptureStatus] = useState<string>('');
  const [templateForCapture, setTemplateForCapture] = useState<EmailTemplate | null>(null);

  const fetchTemplates = async () => {
    if (!user?.organisationId) {
      const errorMessage = 'No organisation access available';
      setError(errorMessage);
      setLoading(false);
      if (onError) onError(new Error(errorMessage));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('[TemplatesTab] Fetching templates for org:', user.organisationId);
      const fetchedTemplates = await EmailService.fetchTemplates(user.organisationId);

      console.log('[TemplatesTab] Fetched templates:', fetchedTemplates.length);
      setTemplates(fetchedTemplates);
    } catch (err) {
      console.error('Error fetching templates:', err);
      const errorMessage = 'Failed to load templates. Please try again.';
      setError(errorMessage);
      if (onError && err instanceof Error) onError(err);

      toast({
        title: 'Error loading templates',
        description: 'There was a problem fetching your templates.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [user]);

  // Fetch recent customers for preview
  useEffect(() => {
    const fetchRecentCustomers = async () => {
      if (!user?.organisationId) return;

      try {
        console.log('[TemplatesTab] Fetching recent customers for preview');
        const customers = await EmailService.getRecentCustomers(user.organisationId, 50);
        setRecentCustomers(customers);

        // Auto-select first customer
        if (customers.length > 0) {
          setSelectedCustomerId(customers[0].id);
        }
      } catch (error) {
        console.error('[TemplatesTab] Error fetching customers:', error);
      }
    };

    fetchRecentCustomers();
  }, [user?.organisationId]);

  // Check if user can create templates
  const canCreateTemplate = user && (
    hasSuperAdminAccess(user.role) ||
    hasOrgAdminAccess(user.role) ||
    hasRestAdminAccess(user.role)
  );

  // View transition handlers
  const handleCreateTemplate = () => {
    setCurrentView('create');
    setSelectedTemplateId(null);
    setSelectedTemplate(null);
    setCustomizedDesignJson(null);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    console.log('[TemplatesTab] Editing template:', template.template_name, 'is_system:', template.is_system_template);

    if (template.is_system_template) {
      // System template: Show customization modal first
      console.log('[TemplatesTab] Opening customization modal for system template');
      setTemplateToCustomize(template);
      setShowCustomizationModal(true);
    } else {
      // User template: Load directly into editor
      console.log('[TemplatesTab] Loading user template directly into editor');
      setSelectedTemplateId(template.id);
      setSelectedTemplate(template);
      setCustomizedDesignJson(null);
      setCurrentView('edit');
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedTemplateId(null);
    setSelectedTemplate(null);
    setCustomizedDesignJson(null);
    fetchTemplates(); // Refresh list
  };

  const handleTemplateSaved = async (savedTemplate?: EmailTemplate) => {
    // Auto-capture preview for new templates without preview
    if (savedTemplate && !savedTemplate.preview_image_url) {
      console.log('[TemplatesTab] Auto-capturing preview for new template:', savedTemplate.template_name);

      // Capture in background (non-blocking)
      handleCapturePreview(savedTemplate).catch(error => {
        console.warn('[TemplatesTab] Auto-capture failed (non-blocking):', error);
        // Don't show error toast for auto-capture - it's optional
      });
    }

    setCurrentView('list');
    setSelectedTemplateId(null);
    setSelectedTemplate(null);
    setCustomizedDesignJson(null);
    fetchTemplates(); // Refresh list with new template
  };

  // Handle customization complete (for system templates)
  const handleCustomizationComplete = (designJson: Record<string, any>) => {
    console.log('[TemplatesTab] Customization complete, loading into editor');
    setShowCustomizationModal(false);
    setCustomizedDesignJson(designJson);
    setSelectedTemplate(templateToCustomize);
    setSelectedTemplateId(templateToCustomize?.id || null);
    setCurrentView('edit');
  };

  // Handle template deletion (show confirmation dialog)
  const handleDeleteTemplate = (template: EmailTemplate) => {
    console.log('[TemplatesTab] Delete requested for:', template.template_name);

    // Check if system template (extra safety check on frontend)
    if (template.is_system_template) {
      toast({
        title: 'Cannot delete system template',
        description: 'System templates are read-only. You can duplicate them to create your own version.',
        variant: 'destructive'
      });
      return;
    }

    // Show confirmation dialog
    setTemplateToDelete(template);
    setShowDeleteDialog(true);
  };

  // Confirm and execute deletion
  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      console.log('[TemplatesTab] Deleting template:', templateToDelete.id);

      await EmailService.deleteTemplate(templateToDelete.id);

      toast({
        title: 'Template deleted',
        description: 'The template has been successfully deleted.',
      });

      // Update local state to remove deleted template
      setTemplates(prev => prev.filter(t => t.id !== templateToDelete.id));

      // Reset dialog state
      setShowDeleteDialog(false);
      setTemplateToDelete(null);

    } catch (err: any) {
      console.error('Error deleting template:', err);

      toast({
        title: 'Error deleting template',
        description: err.message || 'There was a problem deleting the template.',
        variant: 'destructive'
      });

      // Keep dialog open on error so user can retry or cancel
    }
  };

  // Cancel deletion
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setTemplateToDelete(null);
  };

  // Handle template duplication
  const handleDuplicateTemplate = async (template: EmailTemplate) => {
    try {
      console.log('[TemplatesTab] Duplicating template:', template.template_name);

      if (template.is_system_template) {
        setTemplateToCustomize(template);
        setShowCustomizationModal(true);
        return;
      }

      const duplicateName = `${template.template_name} (Copy)`;
      await EmailService.createTemplate({
        template_name: duplicateName,
        template_subject: template.template_subject,
        template_content_html: template.template_content_html,
        design_json: template.design_json,
        template_description: template.description,
        category: template.category,
        organisation_id: template.organisation_id,
        restaurant_id: template.restaurant_id,
        is_system_template: false,
        is_active: true,
      });

      toast({
        title: 'Template duplicated',
        description: `"${duplicateName}" has been created.`,
      });

      fetchTemplates();
    } catch (err) {
      console.error('Error duplicating template:', err);
      toast({
        title: 'Error duplicating template',
        description: 'There was a problem duplicating the template.',
        variant: 'destructive'
      });
    }
  };

  // Handle template preview - Show customer selector first
  const handlePreviewTemplate = (template: EmailTemplate) => {
    console.log('[TemplatesTab] Preview requested for template:', template.template_name);

    // System templates must be customized before preview
    if (template.is_system_template) {
      toast({
        title: 'Customize Template First',
        description: 'System templates must be customized with your branding before preview. Click "Customize" and then preview from the editor.',
        variant: 'default'
      });
      return;
    }

    // Check if we have customers
    if (recentCustomers.length === 0) {
      toast({
        title: 'No Customers Available',
        description: 'Add customers before previewing templates with variable resolution.',
        variant: 'destructive'
      });
      return;
    }

    // Store template and show customer selector
    setTemplateToPreview(template);
    setShowCustomerSelector(true);
  };

  // Generate preview with selected customer
  const handleGeneratePreview = async () => {
    if (!selectedCustomerId || !templateToPreview || !user?.organisationId) {
      toast({
        title: 'Error',
        description: 'Please select a customer',
        variant: 'destructive'
      });
      return;
    }

    setPreviewLoading(true);
    try {
      console.log('[TemplatesTab] Generating preview with customer:', selectedCustomerId);

      // Call backend to resolve variables with real customer data
      const preview = await EmailService.previewTemplateWithCustomer(
        templateToPreview.id,
        selectedCustomerId,
        user.organisationId
      );

      // Close customer selector
      setShowCustomerSelector(false);

      // Open preview dialog
      setResolvedPreview(preview);
      setTemplateBeingPreviewed(templateToPreview); // Track which template is being previewed
      setShowPreviewDialog(true);

      toast({
        title: 'Success',
        description: 'Preview generated with actual customer data',
        variant: 'default'
      });
    } catch (error: any) {
      console.error('[TemplatesTab] Error generating preview:', error);
      toast({
        title: 'Error Generating Preview',
        description: error.message || 'Failed to generate preview',
        variant: 'destructive'
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle template preview capture (screenshot)
  const handleCapturePreview = async (template: EmailTemplate) => {
    if (!user?.organisationId) {
      toast({
        title: 'Cannot capture preview',
        description: 'Organisation not found',
        variant: 'destructive'
      });
      return;
    }

    // Check if we have customers for real data
    if (recentCustomers.length === 0) {
      toast({
        title: 'No Customers Available',
        description: 'Add customers before capturing template previews.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setCapturingPreview(true);
      setTemplateForCapture(template);
      setCaptureStatus('Resolving variables with customer data...');

      console.log('[TemplatesTab] Starting preview capture for:', template.template_name);

      // Step 1: Use first customer for preview (or could add customer selection UI)
      const previewCustomer = recentCustomers[0];
      console.log('[TemplatesTab] Using customer for preview:', previewCustomer.email);

      // Step 2: Get real preview with resolved variables from backend
      const preview = await EmailService.previewTemplateWithCustomer(
        template.id,
        previewCustomer.id,
        user.organisationId
      );

      // Step 3: Open preview dialog with real data
      setCaptureStatus('Opening preview...');
      setResolvedPreview(preview);
      setShowPreviewDialog(true);

      // Step 3: Wait for dialog to open and render
      setCaptureStatus('Waiting for preview to render...');
      await waitForPreviewDialog(10000); // Wait up to 10 seconds

      // Step 4: Additional wait for content to fully load (images, fonts, styles)
      setCaptureStatus('Loading preview content...');
      await new Promise(resolve => setTimeout(resolve, 3000)); // 3 seconds for full content load

      // Step 5: Capture screenshot
      setCaptureStatus('Capturing screenshot...');
      console.log('[TemplatesTab] Capturing screenshot...');
      const screenshot = await capturePreviewDialogScreenshot(500); // Additional 500ms for final render

      // Step 5: Close preview dialog
      setShowPreviewDialog(false);

      // Step 6: Process image
      setCaptureStatus('Processing image...');
      console.log('[TemplatesTab] Processing image...');
      const processed = await processTemplatePreview(screenshot);

      // Step 7: Upload to storage
      setCaptureStatus('Uploading to storage...');
      console.log('[TemplatesTab] Uploading to storage...');
      const publicUrl = await uploadTemplatePreview(template.id, processed);

      // Step 8: Update template record
      setCaptureStatus('Updating template...');
      console.log('[TemplatesTab] Updating template record...');
      await EmailService.updateTemplate(template.id, {
        preview_image_url: publicUrl
      });

      // Step 9: Refresh templates list to show new preview
      await fetchTemplates();

      // Step 10: Cleanup old previews (non-blocking)
      deleteOldTemplatePreviews(template.id).catch(err => {
        console.warn('[TemplatesTab] Cleanup failed (non-critical):', err);
      });

      toast({
        title: 'Preview captured!',
        description: `Preview image saved for "${template.template_name}"`,
      });

      console.log('[TemplatesTab] Preview capture complete');

    } catch (error) {
      console.error('[TemplatesTab] Preview capture failed:', error);

      // Close preview dialog if it's still open
      setShowPreviewDialog(false);

      toast({
        title: 'Capture failed',
        description: error instanceof Error ? error.message : 'Failed to capture preview',
        variant: 'destructive'
      });
    } finally {
      setCapturingPreview(false);
      setCaptureStatus('');
      setTemplateForCapture(null);
    }
  };

  // Render template editor (create/edit)
  if (currentView === 'create' || currentView === 'edit') {
    return (
      <TemplateEditorView
        template={selectedTemplate}
        customizedDesignJson={customizedDesignJson}
        organisationId={user?.organisationId || ''}
        restaurantId={user?.restaurantId}
        onSave={handleTemplateSaved}
        onCancel={handleBackToList}
      />
    );
  }

  // Empty state - no templates
  if (!loading && !error && templates.length === 0) {
    return (
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Email Templates</h3>
          {canCreateTemplate && (
            <Button onClick={handleCreateTemplate} className="gap-1">
              <Plus className="h-4 w-4" />
              Create Template
            </Button>
          )}
        </div>

        <EmptyPlaceholder
          title="No email templates yet"
          description="Create reusable email templates to speed up your campaign creation process."
          icon={<AlertCircle className="h-12 w-12" />}
        >
          {canCreateTemplate && (
            <Button onClick={handleCreateTemplate}>
              New Template
            </Button>
          )}
        </EmptyPlaceholder>

        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Template Benefits</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Save Time</h4>
                <p className="text-sm text-muted-foreground">
                  Create templates once and reuse them across multiple campaigns
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Consistent Branding</h4>
                <p className="text-sm text-muted-foreground">
                  Maintain brand consistency across all your email communications
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Dynamic Content</h4>
                <p className="text-sm text-muted-foreground">
                  Use variables like {"{{firstName}}"} to personalize emails automatically
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Email Templates</h3>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchTemplates}
              disabled={loading}
              size="icon"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>

            {canCreateTemplate && (
              <Button onClick={handleCreateTemplate} className="gap-1">
                <Plus className="h-4 w-4" />
                Create Template
              </Button>
            )}
          </div>
        </div>

        <Separator />

        {/* Template List with Tabs, Search, and Filter */}
        <TemplateList
          templates={templates}
          onTemplateEdit={handleEditTemplate}
          onTemplateDelete={handleDeleteTemplate}
          onTemplateDuplicate={handleDuplicateTemplate}
          onTemplatePreview={handlePreviewTemplate}
          onCapturePreview={handleCapturePreview}
          loading={loading}
          userRole={user?.role}
        />
      </div>

      {/* Template Customization Modal (for system templates) */}
      {templateToCustomize && (
        <TemplateCustomizationModal
          open={showCustomizationModal}
          onClose={() => {
            setShowCustomizationModal(false);
            setTemplateToCustomize(null);
          }}
          template={templateToCustomize}
          organisationId={user?.organisationId || ''}
          restaurantId={user?.restaurantId}
          onComplete={handleCustomizationComplete}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        template={templateToDelete}
      />

      {/* Customer Selector Dialog */}
      <Dialog open={showCustomerSelector} onOpenChange={setShowCustomerSelector}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Preview with Customer Data</DialogTitle>
            <DialogDescription>
              Select a customer to preview this template with their actual data
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Template Info */}
            <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{templateToPreview?.template_name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {templateToPreview?.template_subject}
                </p>
              </div>
            </div>

            {/* Customer Selector */}
            <div className="space-y-2">
              <Label htmlFor="customer-select">Select Customer</Label>
              <Select
                value={selectedCustomerId || ''}
                onValueChange={setSelectedCustomerId}
              >
                <SelectTrigger id="customer-select">
                  <SelectValue placeholder="Choose a customer" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {recentCustomers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>
                          {customer.first_name} {customer.last_name}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {customer.email}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Variables will be replaced with this customer's actual data
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCustomerSelector(false)}
              disabled={previewLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGeneratePreview}
              disabled={!selectedCustomerId || previewLoading}
            >
              {previewLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Generate Preview
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      {resolvedPreview && (
        <PreviewDialog
          open={showPreviewDialog}
          onOpenChange={setShowPreviewDialog}
          resolvedPreview={resolvedPreview}
          template={templateBeingPreviewed ? {
            id: templateBeingPreviewed.id,
            template_name: templateBeingPreviewed.template_name,
            is_system_template: templateBeingPreviewed.is_system_template
          } : undefined}
          userRole={user?.role}
          onPreviewCaptured={fetchTemplates} // Refresh templates list after capture
        />
      )}

      {/* Preview Capture Loading Overlay */}
      {capturingPreview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-2xl max-w-md w-full mx-4">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <Camera className="h-8 w-8 text-primary" />
                <Loader2 className="h-4 w-4 animate-spin absolute -top-1 -right-1 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">Capturing Preview</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {captureStatus || 'Processing...'}
                </p>
              </div>

              {templateForCapture && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Template:</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {templateForCapture.template_name}
                  </p>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                This will take a few seconds. Please wait...
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TemplatesTab;
